import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { categories, categoryImages } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const closeAll = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const isTransparent = isHome && !scrolled && !mobileMenuOpen;

  const mobileMenu = mobileMenuOpen && (
    <div className="fixed inset-0 z-[999] md:hidden">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={closeAll}
        aria-hidden="true"
      />
      <div className="absolute top-0 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto animate-slide-in">
        <div className="flex items-center justify-between h-16 px-4 border-b border-background-200/70">
          <Link
            to="/"
            className="flex items-center gap-2.5"
            onClick={closeAll}
          >
            <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
              <span className="font-bold text-sm text-background-50 font-label">AC</span>
            </div>
            <span className="font-semibold text-base text-foreground-950 font-label">ACBuy</span>
          </Link>
          <button
            onClick={closeAll}
            className="w-9 h-9 flex items-center justify-center text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="px-4 py-5 flex flex-col gap-2">
          <Link
            to="/"
            onClick={closeAll}
            className="flex items-center gap-3 text-foreground-800 text-base font-semibold py-3.5 px-3 rounded-xl hover:bg-background-100 active:bg-background-200 transition-colors font-label"
          >
            <div className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-home-4-line text-lg text-foreground-700"></i>
            </div>
            Home
          </Link>

          <Link
            to="/article/acbuyspreadsheet-complete-guide"
            onClick={closeAll}
            className="flex items-center gap-3 text-foreground-800 text-base font-semibold py-3.5 px-3 rounded-xl hover:bg-background-100 active:bg-background-200 transition-colors font-label"
          >
            <div className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-book-open-line text-lg text-foreground-700"></i>
            </div>
            Shopping Guide
          </Link>

          <Link
            to="/article/acbuyspreadsheet-beginner-tutorial"
            onClick={closeAll}
            className="flex items-center gap-3 text-foreground-700 text-base font-medium py-3.5 px-3 rounded-xl hover:bg-background-100 active:bg-background-200 transition-colors font-label"
          >
            <div className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-article-line text-lg text-foreground-700"></i>
            </div>
            Blog
          </Link>

          <div className="border-t border-background-200/70 my-3"></div>

          <p className="text-foreground-400 text-xs font-semibold uppercase tracking-wider px-3 py-1 font-label">
            Categories
          </p>

          <div className="grid grid-cols-2 gap-1.5">
            {categories.map((cat) => (
              <a
                key={cat.slug}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeAll}
                className="flex items-center gap-2.5 text-foreground-700 text-sm py-2.5 px-3 rounded-xl hover:bg-background-100 active:bg-background-200 transition-colors font-label"
              >
                <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img
                    src={categoryImages[cat.slug] || categoryImages.shoes}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <span className="truncate">{cat.name}</span>
              </a>
            ))}
          </div>

          <div className="border-t border-background-200/70 my-3"></div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            onClick={closeAll}
            className="flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-background-50 px-5 py-4 rounded-full text-base font-semibold cursor-pointer whitespace-nowrap font-label transition-colors"
          >
            buy it now
            <i className="ri-arrow-right-line text-lg"></i>
          </a>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            onClick={closeAll}
            className="flex items-center justify-center gap-2 border-2 border-foreground-200 text-foreground-800 px-5 py-4 rounded-full text-base font-semibold cursor-pointer whitespace-nowrap font-label hover:bg-background-100 transition-colors mt-1"
          >
            acbuy spreadsheet
            <i className="ri-external-link-line text-lg"></i>
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isTransparent
            ? 'bg-transparent'
            : 'bg-background-50/95 backdrop-blur-md shadow-sm'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-[72px]">
          <div className="flex items-center gap-6 md:gap-8">
            <Link
              to="/"
              className="flex items-center gap-2.5 group"
              onClick={closeAll}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                isTransparent ? 'bg-white/20 backdrop-blur-sm' : 'bg-primary-500'
              }`}>
                <span className={`font-bold text-sm font-label transition-colors ${
                  isTransparent ? 'text-white' : 'text-background-50'
                }`}>AC</span>
              </div>
              <span className={`font-semibold text-base font-label whitespace-nowrap transition-colors ${
                isTransparent ? 'text-white' : 'text-foreground-950'
              }`}>
                ACBuy
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-5">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors whitespace-nowrap font-label ${
                  isTransparent
                    ? 'text-white/90 hover:text-white'
                    : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                Home
              </Link>

              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap font-label cursor-pointer ${
                    isTransparent
                      ? 'text-white/90 hover:text-white'
                      : 'text-foreground-700 hover:text-foreground-950'
                  }`}
                >
                  Categories
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)}></div>
                    <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 py-3 shadow-lg z-50 max-h-[460px] overflow-y-auto">
                      {categories.map((cat) => (
                        <a
                          key={cat.slug}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          onClick={closeAll}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap font-label"
                        >
                          <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            <img
                              src={categoryImages[cat.slug] || categoryImages.shoes}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <Link
                to="/article/acbuyspreadsheet-complete-guide"
                className={`text-sm font-medium transition-colors whitespace-nowrap font-label ${
                  isTransparent
                    ? 'text-white/90 hover:text-white'
                    : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                Shopping Guide
              </Link>

              <Link
                to="/article/acbuyspreadsheet-beginner-tutorial"
                className={`text-sm font-medium transition-colors whitespace-nowrap font-label ${
                  isTransparent
                    ? 'text-white/80 hover:text-white'
                    : 'text-foreground-500 hover:text-foreground-950'
                }`}
              >
                Blog
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap font-label"
            >
              buy it now
              <i className="ri-arrow-right-line text-base"></i>
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer transition-colors ${
                isTransparent ? 'text-white' : 'text-foreground-700'
              }`}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mounted && createPortal(mobileMenu, document.body)}
    </>
  );
}